import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const NAV_LINKS = [
  { label: '首页', path: '/' },
  { label: '电影', path: '/category/电影' },
  { label: '电视剧', path: '/category/电视剧' },
  { label: '综艺', path: '/category/综艺' },
  { label: '搜索', path: '/search' },
  { label: '关于', path: '/about' },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : '/search');
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
          : 'bg-background-50'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-[72px] gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0 group">
            <span className="w-10 h-10 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center font-heading font-extrabold text-lg">
              91
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-extrabold text-lg md:text-xl text-foreground-950 tracking-tight">
                91大神
              </span>
              <span className="text-[10px] md:text-[11px] text-foreground-500 tracking-[0.2em] font-label">
                DASHEN CINEMA
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => {
              const active =
                link.path === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(link.path.split('?')[0]);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3.5 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    active
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <form
              onSubmit={handleSearch}
              className="hidden md:flex items-center h-10 w-56 xl:w-72 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-400 transition-colors px-4"
            >
              <i className="ri-search-line text-foreground-500 text-base" />
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视名称"
                className="flex-1 bg-transparent border-none outline-none text-sm text-foreground-900 placeholder:text-foreground-400 px-2"
              />
            </form>

            <Link
              to="/search"
              className="md:hidden w-10 h-10 rounded-full bg-background-100 flex items-center justify-center text-foreground-700 cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg" />
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              className="lg:hidden w-10 h-10 rounded-full bg-background-100 flex items-center justify-center text-foreground-800 cursor-pointer"
              aria-label="菜单"
            >
              <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 px-4 py-4">
          <form
            onSubmit={handleSearch}
            className="flex items-center h-11 w-full rounded-full bg-background-100 border border-background-200 px-4 mb-3"
          >
            <i className="ri-search-line text-foreground-500 text-base" />
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视名称"
              className="flex-1 bg-transparent border-none outline-none text-sm text-foreground-900 placeholder:text-foreground-400 px-2"
            />
            <button type="submit" className="text-primary-600 text-sm font-medium cursor-pointer">
              搜索
            </button>
          </form>
          <div className="grid grid-cols-2 gap-2">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="px-4 py-3 rounded-lg bg-background-100 text-foreground-800 text-sm font-medium cursor-pointer"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}